import * as React from 'react';
import type { Stripe } from '@stripe/stripe-js';
import { loadStripe } from '@stripe/stripe-js/pure';

const stripePromises = new Map<string, Promise<Stripe | null>>();

const getStripePromise = (publishableKey: string): Promise<Stripe | null> => {
  const existingPromise = stripePromises.get(publishableKey);
  if (existingPromise) {
    return existingPromise;
  }

  const promise = loadStripe(publishableKey).catch((error) => {
    if (stripePromises.get(publishableKey) === promise) {
      stripePromises.delete(publishableKey);
    }
    throw error;
  });
  stripePromises.set(publishableKey, promise);
  return promise;
};

export const useStripeLoader = (enabled: boolean, publishableKey: string) => {
  const [stripe, setStripe] = React.useState<Stripe | null>(null);
  const [error, setError] = React.useState('');
  const [isLoading, setIsLoading] = React.useState(false);
  const [attempt, setAttempt] = React.useState(0);

  React.useEffect(() => {
    setStripe(null);
    setError('');
    if (!enabled || !publishableKey) {
      setIsLoading(false);
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    getStripePromise(publishableKey)
      .then((loadedStripe) => {
        if (cancelled) return;
        if (!loadedStripe) {
          throw new Error('Stripe.js did not initialise.');
        }
        setStripe(loadedStripe);
      })
      .catch(() => {
        if (!cancelled) {
          setError('The secure payment form could not load. Check your connection or privacy settings and try again.');
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [attempt, enabled, publishableKey]);

  const retry = React.useCallback(() => {
    stripePromises.delete(publishableKey);
    setAttempt((currentAttempt) => currentAttempt + 1);
  }, [publishableKey]);

  return { stripe, error, isLoading, retry };
};
